/**
 * `/voice` — G8 hands-free voice mode (PWA foundation; native iOS/watchOS hosts come later and
 * reuse the same machine, intents, summary and distillation modules).
 *
 * Hold to talk (or turn on hands-free), ask "what's happening", drill into a project, answer the
 * first question, discuss and scope with the project's triage agent (streamed replies are read
 * aloud sentence by sentence; talking over it interrupts), then "wrap up" to review the
 * distilled goal/plan/task proposals. Everything spoken is also captioned; every control works
 * by keyboard; Escape cancels.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type DistillProposal, GoalRecord, goalEntityId } from '@viko/protocol'
import { Button } from '@viko/transcript-ui'
import { Hand, Keyboard, Mic, MicOff, Square } from 'lucide-react'
import { type FormEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { ApiError } from '../../lib/api.js'
import { describeAsk } from '../../lib/ask-view.js'
import { CommandRejected, useBackend } from '../../lib/backend.js'
import { pickTriageMachine } from '../../lib/bark-store.js'
import { useDigests } from '../../lib/digests.js'
import { loadGoal } from '../../lib/goals.js'
import { answerOnce } from '../../lib/private-inbox.js'
import {
  useInbox,
  useMachines,
  useProjects,
  useRuns,
  useTasks,
  useWorkstreams,
} from '../../lib/queries.js'
import { type ResearchEntry, researchForConversation, useResearch } from '../../lib/research.js'
import { useRunTranscript } from '../../lib/session.js'
import type { InboxItem } from '../../lib/types.js'
import { answerFor, replyAfter, turnCount } from '../../lib/voice/answers.js'
import { distillConversation } from '../../lib/voice/distill.js'
import { matchProject, parseIntent, VOICE_HELP } from '../../lib/voice/intents.js'
import {
  askAt,
  buildVoiceSummary,
  projectDetail,
  type SummaryAsk,
  spokenAsks,
  type VoiceSummary,
} from '../../lib/voice/summary.js'
import { type TurnHandler, useVoice, type VoiceTurn } from '../../lib/voice/useVoice.js'
import { useBark, useBarkStore } from '../bark/useBarks.js'
import { useNeedsYou } from '../inbox/useNeedsYou.js'
import { Input, PageHead } from '../ui.js'
import { DistillReview } from './DistillReview.js'

interface LogLine {
  id: number
  who: 'you' | 'viko'
  text: string
}
interface Discussion {
  projectId: string
  barkId: string
}
interface PendingReply {
  turn: VoiceTurn
  sentAt: number
  /** Turns in the item transcript when the message went out (null: session not started yet). */
  baseline: number | null
  spokeText: string
}

const STATUS_LABEL = {
  idle: 'Hold to talk',
  listening: 'Listening…',
  thinking: 'Working on it…',
  speaking: 'Speaking — press to interrupt',
} as const

const answerIds = new Map<string, string>()

export function VoiceMode() {
  const b = useBackend()
  const projects = useProjects().data ?? []
  const runs = useRuns().data ?? []
  const inbox = useInbox().data ?? []
  const needs = useNeedsYou()
  const digests = useDigests().data ?? []
  const machines = useMachines().data ?? []
  const barks = useBarkStore()
  const research = useResearch().data ?? []
  const ents = b.entities
  // Goals from the encrypted `goal` entities (one per project), for the drill-down.
  const goals = useQuery({
    queryKey: ['voice-goals', !!ents],
    queryFn: async () => {
      const out = new Map<string, string>()
      for (const row of ents ? await ents.list('goal') : []) {
        const g = GoalRecord.safeParse(row.value)
        if (g.success && row.id === goalEntityId(g.data.projectId))
          out.set(g.data.projectId, g.data.text)
      }
      return out
    },
    enabled: !!ents,
    staleTime: 60_000,
  }).data
  const [log, setLog] = useState<LogLine[]>([])
  const [discussion, setDiscussion] = useState<Discussion | null>(null)
  const [cards, setCards] = useState<DistillProposal[] | null>(null)
  const [typed, setTyped] = useState('')
  const typedId = useId()
  const pending = useRef<PendingReply | null>(null)
  /** Turns in the open discussion's transcript right now (the baseline for the next reply). */
  const turns = useRef<number | null>(null)
  const lastSpoken = useRef('')
  const awaitingTopic = useRef<string | null>(null)
  const distillRef = useRef<() => DistillProposal[]>(() => [])
  const seq = useRef(0)

  const say = useCallback((who: LogLine['who'], text: string) => {
    setLog((l) => [...l.slice(-49), { id: ++seq.current, who, text }])
  }, [])

  // Verified state → the summary (recomputed per render so it is never stale).
  const projectOfRun = useMemo(() => new Map(runs.map((r) => [r.runId, r.projectId])), [runs])
  const askItems = useMemo(
    () => inbox.filter((i) => i.type === 'question' || i.type === 'approval'),
    [inbox],
  )
  const summary: VoiceSummary = useMemo(() => {
    const asks: SummaryAsk[] = askItems.map((i) => {
      const d = i.event ? describeAsk(i.event) : { title: i.title, body: i.title }
      const q = i.event?.kind === 'question' ? i.event.questions[0] : undefined
      const projectId =
        projectOfRun.get(i.runId) ??
        (i.projectName ? matchProject(i.projectName, projects)?.id : undefined)
      return {
        id: i.id,
        runId: i.runId,
        ...(projectId ? { projectId } : {}),
        kind: i.type === 'approval' ? 'approval' : 'question',
        title: d.title,
        body: d.body,
        blocking: i.blocking,
        createdAt: i.createdAt,
        ...(q ? { options: q.options.map((o) => o.label) } : {}),
      }
    })
    return buildVoiceSummary({
      now: Date.now(),
      projects: projects.map((p) => ({
        id: p.id,
        name: p.name,
        ...((goals?.get(p.id) ?? p.goal) ? { goal: goals?.get(p.id) ?? p.goal } : {}),
      })),
      runs: runs.map((r) => ({
        runId: r.runId,
        ...(r.projectId ? { projectId: r.projectId } : {}),
        status: String(r.status),
        ...(r.title ? { title: r.title } : {}),
        ...(r.blockedOn ? { blockedOn: r.blockedOn } : {}),
      })),
      asks,
      digests,
      needsYou: { total: needs.total, of: needs.of }, // one count source: lib/needs-you.ts
    })
  }, [askItems, projectOfRun, projects, runs, digests, goals, needs])
  const summaryRef = useRef(summary)
  summaryRef.current = summary

  const deliver = async <K extends 'run.answer' | 'run.approve'>(
    item: InboxItem,
    kind: K,
    payload: Parameters<typeof b.dispatch<K>>[2],
  ) => {
    if (!item.machineId) throw new Error('That machine is not reachable right now')
    const machineId = item.machineId
    if (!item.private) return void (await b.dispatch(kind, machineId, payload))
    const r = await answerOnce(
      {
        claim: (id, commandId) => b.api.answerClaim(id, commandId),
        send: (commandId) => b.dispatch(kind, machineId, payload, { id: commandId }),
        isDuplicate: (e) =>
          (e instanceof ApiError && e.code === 'id_conflict') ||
          (e instanceof CommandRejected && e.outcome.status === 'duplicate'),
      },
      answerIds,
      item.itemId ?? item.id,
    )()
    if (r === 'already-answered') throw new Error('Already answered on another device')
  }

  const startDiscussion = async (projectId: string, topic: string, turn: VoiceTurn) => {
    const project = projects.find((p) => p.id === projectId)
    if (!barks?.available)
      return turn.say('Discussions are end-to-end encrypted. Unlock this browser first.')
    const machineId = pickTriageMachine(project, machines)
    if (!machineId) return turn.say(`No machine can host ${project?.name ?? 'that'}'s agent.`)
    const rec = await barks.submit({
      projectId,
      machineId,
      text: topic,
      mode: 'discuss',
      dictated: true,
      source: 'capture',
    })
    setCards(null)
    setDiscussion({ projectId, barkId: rec.id })
    pending.current = { turn, sentAt: Date.now(), baseline: null, spokeText: '' }
  }

  const handle: TurnHandler = async (text, turn) => {
    say('you', text)
    const reply = (t: string) => {
      lastSpoken.current = t
      say('viko', t)
      turn.say(t)
    }
    const s = summaryRef.current
    if (awaitingTopic.current) {
      const projectId = awaitingTopic.current
      awaitingTopic.current = null
      const i = parseIntent(text, { projects, discussing: false })
      if (i.kind === 'cancel') return turn.done()
      return startDiscussion(projectId, text, turn)
    }
    const intent = parseIntent(text, { projects, discussing: !!discussion })
    switch (intent.kind) {
      case 'overview':
        return reply(s.spoken)
      case 'project':
        return reply(projectDetail(s, intent.projectId))
      case 'unknown-project':
        return reply(`I don't know a project called ${intent.name}.`)
      case 'asks':
        return reply(spokenAsks(s))
      case 'answer':
      case 'approve': {
        const ask = askAt(s, intent.index)
        if (!ask) return reply('There is no such question. Say “read the questions” to hear them.')
        const item = askItems.find((i) => i.id === ask.id && i.runId === ask.runId)
        if (!item) return reply('That question was already answered.')
        if (intent.kind === 'approve') {
          if (item.type !== 'approval')
            return reply('That one is a question. Say “answer”, then your answer.')
          await deliver(item, 'run.approve', {
            runId: item.runId,
            approvalId: item.id,
            decision: intent.decision,
          })
          return reply(intent.decision === 'allow' ? 'Approved.' : 'Denied.')
        }
        if (item.type !== 'question' || item.event?.kind !== 'question')
          return reply('That one is an approval. Say approve or deny.')
        const qs = item.event.questions
        const first = qs[0]!
        const labels = answerFor(first, intent.answer)
        if (!labels)
          return reply(
            `That doesn't match an option. The options are ${first.options.map((o) => o.label).join(', ')}.`,
          )
        if (qs.length > 1)
          return reply(
            `That ask has ${qs.length} questions; open it in the Inbox to answer all of them.`,
          )
        await deliver(item, 'run.answer', {
          runId: item.runId,
          questionId: item.id,
          answers: { [first.id]: labels },
        })
        return reply(`Answered: ${labels.join(', ')}.`)
      }
      case 'discuss': {
        const name = projects.find((p) => p.id === intent.projectId)?.name ?? 'that project'
        if (!intent.text) {
          awaitingTopic.current = intent.projectId
          return reply(`What would you like to discuss with ${name}?`)
        }
        say('viko', `Discussing with ${name}'s agent.`)
        return startDiscussion(intent.projectId, intent.text, turn)
      }
      case 'say': {
        if (!discussion || !barks) return reply('Start a discussion first.')
        const bark = await barks.get(discussion.barkId)
        if (!bark) return reply('The discussion is not available yet. Try again in a moment.')
        const baseline = turns.current
        await barks.message(bark, text)
        pending.current = { turn, sentAt: Date.now(), baseline, spokeText: '' }
        return
      }
      case 'distill': {
        if (!discussion) return reply('There is no discussion to wrap up.')
        const next = distillRef.current()
        setCards(next)
        if (!next.length)
          return reply(
            'Nothing to distill yet. Ask the agent to summarize a goal, a plan and tasks.',
          )
        return reply(
          `${next.length} ${next.length === 1 ? 'proposal' : 'proposals'} to review: ${next
            .map((c) => c.title)
            .join('; ')}. Review them on screen.`,
        )
      }
      case 'end':
        setDiscussion(null)
        pending.current = null
        return reply('Discussion ended. It stays in the project with everything it produced.')
      case 'cancel':
        return turn.done()
      case 'repeat':
        return lastSpoken.current ? turn.say(lastSpoken.current) : reply('Nothing to repeat yet.')
      case 'help':
        return reply(VOICE_HELP)
      case 'unknown':
        return reply('I didn’t catch a command. Say “help” to hear what you can say.')
    }
  }
  const voice = useVoice(handle)
  const { state, dispatch } = voice

  // Escape cancels anywhere on the page; Space held on the talk button is push-to-talk.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        pending.current = null
        dispatch({ type: 'CANCEL' })
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [dispatch])

  const talkKey = (down: boolean) => (e: React.KeyboardEvent) => {
    if (e.key !== ' ' && e.key !== 'Enter') return
    e.preventDefault()
    if (down && e.repeat) return
    dispatch({ type: down ? 'PTT_DOWN' : 'PTT_UP' })
  }
  const submitTyped = (e: FormEvent) => {
    e.preventDefault()
    voice.submitText(typed)
    setTyped('')
  }
  const discussing = discussion
    ? (projects.find((p) => p.id === discussion.projectId)?.name ?? 'project')
    : null

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto">
      <PageHead title="Voice">
        <Button
          size="sm"
          aria-pressed={state.handsFree}
          icon={state.handsFree ? <Mic size={14} /> : <MicOff size={14} />}
          disabled={!voice.recognitionSupported}
          onClick={() => dispatch({ type: 'TOGGLE_HANDS_FREE' })}
        >
          Hands-free {state.handsFree ? 'on' : 'off'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          icon={<Square size={14} />}
          disabled={state.status === 'idle'}
          onClick={() => {
            pending.current = null
            dispatch({ type: 'CANCEL' })
          }}
        >
          Stop
        </Button>
      </PageHead>
      <div className="mx-auto grid w-full max-w-[880px] gap-4 px-4 pb-8">
        <section aria-label="Talk" className="grid justify-items-center gap-2 pt-2">
          <button
            type="button"
            className="voice-talk"
            data-status={state.status}
            aria-describedby="voice-status"
            disabled={!voice.recognitionSupported}
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture?.(e.pointerId)
              dispatch({ type: 'PTT_DOWN' })
            }}
            onPointerUp={() => dispatch({ type: 'PTT_UP' })}
            onPointerCancel={() => dispatch({ type: 'PTT_UP' })}
            onKeyDown={talkKey(true)}
            onKeyUp={talkKey(false)}
          >
            {state.status === 'listening' ? (
              <Mic size={36} aria-hidden="true" />
            ) : (
              <Hand size={36} aria-hidden="true" />
            )}
            <span className="sr-only">Push to talk</span>
          </button>
          <p id="voice-status" className="m-0 text-[15px] text-text-1" role="status">
            {voice.recognitionSupported
              ? state.handsFree && state.status === 'listening'
                ? 'Listening hands-free — just talk'
                : STATUS_LABEL[state.status]
              : 'Speech recognition is not available in this browser — type below.'}
            {discussing ? ` · Discussing with ${discussing}` : ''}
          </p>
          {state.interim ? (
            <p className="m-0 text-[15px] italic text-text-2" aria-hidden="true">
              {state.interim}
            </p>
          ) : null}
          {state.error ? (
            <p className="m-0 text-[14px] text-status-fail" role="alert">
              {state.error}
            </p>
          ) : null}
          <div className="flex flex-wrap justify-center gap-2">
            <Button size="sm" onClick={() => voice.submitText("what's happening")}>
              What’s happening
            </Button>
            <Button size="sm" onClick={() => voice.submitText('read the questions')}>
              Read the questions
            </Button>
            {discussion ? (
              <>
                <Button size="sm" onClick={() => voice.submitText('wrap up')}>
                  Wrap up
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => voice.submitText('end the discussion')}
                >
                  End discussion
                </Button>
              </>
            ) : null}
          </div>
          <form onSubmit={submitTyped} className="flex w-full max-w-[560px] items-center gap-2">
            <label
              htmlFor={typedId}
              className="inline-flex items-center gap-1 text-[14px] text-text-2"
            >
              <Keyboard size={14} aria-hidden="true" /> Type
            </label>
            <Input
              id={typedId}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={
                discussion ? 'Say something to the agent' : 'e.g. tell me more about Viko'
              }
              className="min-w-0 flex-1"
            />
            <Button size="sm" type="submit" disabled={!typed.trim()}>
              Send
            </Button>
          </form>
        </section>

        <section aria-label="Conversation captions">
          <ol role="log" aria-live="polite" className="m-0 grid list-none gap-2 p-0">
            {log.map((l) => (
              <li
                key={l.id}
                className={`max-w-[85%] rounded-xl px-3 py-2 text-[15px] ${
                  l.who === 'you'
                    ? 'justify-self-end bg-accent-soft text-text-1'
                    : 'bg-surface-2 text-text-1'
                }`}
              >
                <span className="sr-only">{l.who === 'you' ? 'You: ' : 'Viko: '}</span>
                {l.text}
              </li>
            ))}
          </ol>
        </section>

        {summary.projects.length ? (
          <section aria-label="Projects" className="grid gap-1">
            <h2 className="m-0 text-[14px] font-semibold text-text-2">Drill into a project</h2>
            <div className="flex flex-wrap gap-2">
              {summary.projects.map((p) => (
                <Button
                  key={p.id}
                  size="sm"
                  onClick={() => voice.submitText(`tell me more about ${p.name}`)}
                >
                  {p.short}
                </Button>
              ))}
            </div>
          </section>
        ) : null}

        {discussion ? (
          <DiscussionPanel
            key={discussion.barkId}
            discussion={discussion}
            pending={pending}
            turns={turns}
            research={research}
            distillRef={distillRef}
            cards={cards}
            onCards={setCards}
            onSpoken={(t) => {
              lastSpoken.current = t
            }}
            onAgent={(t) => say('viko', t)}
          />
        ) : null}
      </div>
    </div>
  )
}

/** The live discussion: streams the agent's reply into speech and holds the distill context. */
function DiscussionPanel({
  discussion,
  pending,
  turns,
  research,
  distillRef,
  cards,
  onCards,
  onSpoken,
  onAgent,
}: {
  discussion: Discussion
  pending: React.MutableRefObject<PendingReply | null>
  turns: React.MutableRefObject<number | null>
  research: ResearchEntry[]
  distillRef: React.MutableRefObject<() => DistillProposal[]>
  cards: DistillProposal[] | null
  onCards: (c: DistillProposal[]) => void
  onSpoken: (t: string) => void
  onAgent: (t: string) => void
}) {
  const { bark, store } = useBark(discussion.projectId, discussion.barkId)
  const tasks = useTasks({ projectId: discussion.projectId }).data ?? []
  const workstreams = useWorkstreams(discussion.projectId).data ?? []
  const project = useProjects().data?.find((p) => p.id === discussion.projectId)
  const ents = useBackend().entities
  // The goal entity is the source of truth; the legacy project field is the fallback.
  const goal = useQuery({
    queryKey: ['voice-goal', discussion.projectId, !!ents],
    queryFn: () => (ents ? loadGoal(ents, discussion.projectId) : null),
    enabled: !!ents,
  }).data
  const currentGoal = goal?.text ?? project?.goal
  const linked = useMemo(
    () => researchForConversation(research, discussion.barkId),
    [research, discussion.barkId],
  )
  distillRef.current = () =>
    bark
      ? distillConversation(bark, {
          ...(currentGoal ? { goal: currentGoal } : {}),
          tasks: tasks.map((t) => ({ id: t.id, title: t.title })),
          workstreams: workstreams.map((w) => ({ id: w.id, name: w.name })),
          research: linked,
        })
      : []

  // Fallback when no live transcript is open: the agent's committed message, spoken whole.
  useEffect(() => {
    const p = pending.current
    if (!p || !bark || p.spokeText) return
    const msg = [...bark.thread].reverse().find((m) => m.author === 'agent' && m.at >= p.sentAt)
    if (!msg) return
    pending.current = null
    onSpoken(msg.text)
    onAgent(msg.text)
    p.turn.stream(msg.text, true)
  }, [bark, pending, onSpoken, onAgent])

  const runId = bark?.triage.itemRunId
  return (
    <section
      aria-label="Discussion"
      className="grid gap-3 rounded-xl border border-border-subtle p-3"
    >
      <div className="flex flex-wrap items-center gap-2 text-[14px]">
        <span className="font-semibold text-text-1">
          Discussing with {project?.name ?? 'the project'}’s agent
        </span>
        <span className="text-text-2">
          · {bark ? bark.thread.length : 0} messages · {linked.length} research{' '}
          {linked.length === 1 ? 'entry' : 'entries'}
        </span>
        <span className="flex-1" />
        <Link
          to="/p/$projectId"
          params={{ projectId: discussion.projectId }}
          search={{ i: `bark:${discussion.barkId}` }}
          className="text-accent-text"
        >
          Open the conversation
        </Link>
      </div>
      {runId ? (
        <ReplyStream
          runId={runId}
          pending={pending}
          turns={turns}
          onDone={(t) => {
            onSpoken(t)
            onAgent(t)
          }}
        />
      ) : (
        <p className="m-0 text-[14px] text-text-2" role="status">
          {bark ? 'The agent is starting…' : 'Sending…'}
        </p>
      )}
      {linked.length ? (
        <ul className="m-0 grid list-none gap-1 p-0 text-[14px]">
          {linked.map((r) => (
            <li key={r.id}>
              Research: <span className="text-text-1">{r.title}</span>{' '}
              <span className="text-text-3">({r.sources.length} sources)</span>
            </li>
          ))}
        </ul>
      ) : null}
      {cards && bark && store ? (
        <DistillReview cards={cards} bark={bark} store={store} onChange={onCards} />
      ) : null}
    </section>
  )
}

/** Streams the item session's reply into the pending voice turn, sentence by sentence. */
function ReplyStream({
  runId,
  pending,
  turns,
  onDone,
}: {
  runId: string
  pending: React.MutableRefObject<PendingReply | null>
  turns: React.MutableRefObject<number | null>
  onDone: (text: string) => void
}) {
  const view = useRunTranscript(runId)
  const count = turnCount(view.entries) + (view.turnBase ?? 0) // materialized-state: windowed
  turns.current = count
  useEffect(
    () => () => {
      turns.current = null
    },
    [turns],
  )
  useEffect(() => {
    const p = pending.current
    if (!p) return
    // No baseline: the message opened the session, so its first turn is the reply.
    const r = replyAfter(view.entries, p.baseline ?? 0)
    if (!r) return
    p.spokeText = r.text
    p.turn.stream(r.text, r.done)
    if (r.done) {
      pending.current = null
      onDone(r.text)
    }
  }, [view.entries, pending, onDone])
  return null
}
